import { useEffect, useState } from 'react'
import { api, session } from '../api.js'
import RuleBuilder from '../components/RuleBuilder.jsx'
import EtapeForm from '../components/EtapeForm.jsx'
import ActionForm from '../components/ActionForm.jsx'

// Le studio de circuits. Une étape, ce qui s'y passe tout seul, ce que l'agent
// peut cliquer. Tout est écrit en français, aucune syntaxe n'est exposée.
export default function Circuits() {
  const [liste, setListe] = useState([])
  const [ouvert, setOuvert] = useState(null)
  const [modeles, setModeles] = useState([])
  const [creation, setCreation] = useState(false)

  const charger = () => api.workflows().then(setListe)
  useEffect(() => { charger(); api.workflowTemplates().then(setModeles) }, [])

  if (ouvert) return <Editeur wfKey={ouvert} onRetour={() => { setOuvert(null); charger() }} />

  return (
    <>
      <h1>Circuits de traitement</h1>
      <p className="chapo">Le trajet d'un dossier : les étapes par lesquelles il passe, ce qui part
        tout seul, et les boutons proposés aux agents.</p>

      {session.env === 'draft' && (
        <button className="btn pri" onClick={() => setCreation(!creation)}>
          {creation ? 'Annuler' : 'Créer un circuit'}
        </button>
      )}

      {creation && (
        <section className="carte">
          <h2>Partir d'un modèle</h2>
          <p className="aide">Tout reste modifiable ensuite : étapes, boutons, courriels.</p>
          <div className="grille">
            {modeles.map((m) => (
              <article className="fiche" key={m.key}>
                <h3>{m.name}</h3>
                <p>{m.description}</p>
                <p className="mono petit">{m.statuses.length} étapes · {m.actions} actions</p>
                <button className="btn sm pri" onClick={async () => {
                  const key = prompt('Identifiant du circuit (lettres minuscules)', m.key + '2')
                  if (!key) return
                  await api.createWorkflow({ template: m.key, key })
                  setCreation(false); charger()
                }}>Utiliser ce modèle</button>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="grille">
        {liste.map((w) => (
          <article className="fiche" key={w.key}>
            <h3>{w.name}</h3>
            <p>{w.description}</p>
            <p className="mono petit">{w.statuses} étapes · {w.actions} actions</p>
            {w.problems > 0 && (
              <p className="probleme">{w.problems} point{w.problems > 1 ? 's' : ''} à corriger</p>
            )}
            <div className="ligne">
              <button className="btn sm pri" onClick={() => setOuvert(w.key)}>Ouvrir</button>
              {session.env === 'draft' && (
                <button className="btn sm dgr" onClick={async () => {
                  if (!confirm(`Supprimer le circuit « ${w.name} » ?`)) return
                  try { await api.deleteWorkflow(w.key); charger() }
                  catch (e) { alert(e.message) }
                }}>Supprimer</button>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  )
}

function Editeur({ wfKey, onRetour }) {
  const [w, setW] = useState(null)
  const [onglet, setOnglet] = useState('parcours')
  const [vocabulaire, setVocabulaire] = useState([])
  const [catalogue, setCatalogue] = useState(null)
  const [message, setMessage] = useState('')

  const charger = () => { api.workflow(wfKey).then(setW) }
  useEffect(() => {
    charger()
    api.vocabulary(wfKey).then((v) => setVocabulaire(v.fields)).catch(() => setVocabulaire([]))
    api.actionCatalog().then(setCatalogue).catch(() => setCatalogue(null))
  }, [wfKey])   // eslint-disable-line

  if (!w || !catalogue) return <p className="chargement">Chargement…</p>
  const lectureSeule = session.env === 'live'

  async function enregistrer(payload) {
    const maj = { ...w, ...payload }
    setW(maj)
    const r = await api.saveWorkflow(wfKey, payload)
    setMessage('Enregistré. Pensez à mettre votre portail à jour.')
    if (r?.problems) setW({ ...maj, problems: r.problems })
  }

  return (
    <>
      <button className="retour" onClick={onRetour}>← Circuits de traitement</button>
      <h1>{w.name}</h1>
      <p className="chapo">{w.description}</p>
      {message && <p className="succes" role="status">{message}</p>}

      <div className="onglets" role="tablist">
        {[['parcours', 'Le parcours'], ['verifier', `Vérifier${w.problems?.length ? ` (${w.problems.length})` : ''}`],
          ['tester', 'Tester']].map(([id, label]) => (
          <button key={id} role="tab" aria-selected={onglet === id}
                  className={onglet === id ? 'on' : ''} onClick={() => setOnglet(id)}>{label}</button>
        ))}
      </div>

      {onglet === 'parcours' && (
        <Parcours w={w} vocabulaire={vocabulaire} catalogue={catalogue}
                  lectureSeule={lectureSeule} onEnregistrer={enregistrer} />
      )}
      {onglet === 'verifier' && <Verifier problemes={w.problems || []} />}
      {onglet === 'tester' && <Simulateur wfKey={wfKey} vocabulaire={vocabulaire} />}
    </>
  )
}

function Parcours({ w, vocabulaire, catalogue, lectureSeule, onEnregistrer }) {
  const [etapeEditee, setEtapeEditee] = useState(null)
  const [actionEditee, setActionEditee] = useState(null)
  const [ajoutSur, setAjoutSur] = useState(null)   // étape où l'on ajoute une action

  const etapeDe = (id) => w.statuses.find((s) => s.id === id)
  const actions = w.actions || []
  const idNouveau = (prefixe) => `${prefixe}_${Math.random().toString(36).slice(2, 8)}`

  const phrase = (a) => {
    const vers = (id) => <strong>{etapeDe(id)?.label || 'étape supprimée'}</strong>
    switch (a.type) {
      case 'jump': return a.kind === 'agent'
        ? <>Bouton <strong>{a.label}</strong> : envoie le dossier vers {vers(a.target)}
           {a.require_comment && ", après saisie d'un motif"}</>
        : <>Part automatiquement vers {vers(a.target)}</>
      case 'delay': return <>Au bout de <strong>{a.delay?.n} {a.delay?.unit}(s)</strong> sans
        mouvement, passe à {vers(a.target)}</>
      case 'ask_user': return <>Bouton <strong>{a.label}</strong> : demande un complément à
        l'habitant et met le dossier en {vers(a.target)}</>
      case 'email': return <>Envoie <strong>{catalogue.mails.find((m) => m.key === a.mail_id)?.name
        || 'un courriel'}</strong> à {a.to === 'demandeur' || !a.to ? "l'habitant" : <strong>{a.to}</strong>}</>
      case 'sms': return <>Envoie un SMS à l'habitant</>
      case 'notify': return <>Affiche à l'habitant : « {(a.text || '').slice(0, 60)} »</>
      case 'alert': return <>Alerte les agents : « {(a.text || '').slice(0, 60)} »</>
      case 'criticity': return <>Passe le dossier en priorité <strong>
        {catalogue.criticities.find((c) => c.key === a.level)?.label || a.level}</strong></>
      case 'document': return <>Génère le document <strong>{a.name}</strong></>
      case 'history': return <>{a.visible ? "Écrit au suivi de l'habitant" : 'Écrit une note interne'}
        : « {(a.text || '').slice(0, 50)} »</>
      case 'webservice': return <>Appelle <strong>{a.label || 'un service externe'}</strong></>
      default: return a.type
    }
  }

  // ---------- étapes ----------
  function sauverEtape(e) {
    const existe = w.statuses.some((s) => s.id === e.id)
    onEnregistrer({ statuses: existe
      ? w.statuses.map((s) => (s.id === e.id ? e : s))
      : [...w.statuses, e] })
    setEtapeEditee(null)
  }
  function supprimerEtape(e) {
    if (w.statuses.length <= 2) {
      alert("Un circuit garde au moins deux étapes.")
      return
    }
    const pointent = actions.filter((a) => a.target === e.id).length
    const message = pointent
      ? `${pointent} action envoie des dossiers vers cette étape. Elles seront supprimées aussi. Continuer ?`
      : `Supprimer l'étape « ${e.label} » ?`
    if (!confirm(message)) return
    onEnregistrer({
      statuses: w.statuses.filter((s) => s.id !== e.id),
      actions: actions.filter((a) => a.from !== e.id && a.target !== e.id),
    })
    setEtapeEditee(null)
  }
  function deplacerEtape(index, sens) {
    const cible = index + sens
    if (cible < 0 || cible >= w.statuses.length) return
    const copie = [...w.statuses]
    ;[copie[index], copie[cible]] = [copie[cible], copie[index]]
    onEnregistrer({ statuses: copie })
  }

  // ---------- actions ----------
  function sauverAction(a) {
    const existe = actions.some((x) => x.id === a.id)
    onEnregistrer({ actions: existe
      ? actions.map((x) => (x.id === a.id ? a : x))
      : [...actions, a] })
    setActionEditee(null)
  }
  function supprimerAction(a) {
    if (!confirm('Supprimer cette action ?')) return
    onEnregistrer({ actions: actions.filter((x) => x.id !== a.id) })
    setActionEditee(null)
  }
  function dupliquerAction(a) {
    const copie = { ...a, id: idNouveau('ac') }
    if (copie.label) copie.label += ' (copie)'
    onEnregistrer({ actions: [...actions, copie] })
    setActionEditee(null)
  }
  function nouvelleAction(modele, etapeId) {
    const suivante = w.statuses[w.statuses.findIndex((s) => s.id === etapeId) + 1] || w.statuses[0]
    const base = { id: idNouveau('ac'), from: etapeId, kind: modele.kind, type: modele.type,
                   condition: '' }
    const defauts = {
      jump: modele.kind === 'agent'
        ? { label: 'Nouveau bouton', target: suivante.id, tone: 'pri', require_comment: false }
        : { label: 'Aiguillage', target: suivante.id },
      delay: { label: 'Sans réponse depuis 15 jours', target: suivante.id,
               delay: { n: 15, unit: 'jour' } },
      ask_user: { label: 'Demander un complément', target: suivante.id, ask: '' },
      email: { mail_id: catalogue.mails[0]?.key || '', to: 'demandeur' },
      sms: { text: 'Votre demande {{ form_number }} : {{ form_status }}.' },
      notify: { text: "Votre dossier avance, aucune action n'est attendue de votre part." },
      alert: { text: 'Dossier à regarder en priorité.' },
      history: { text: '', visible: false },
      document: { name: 'Attestation' },
      criticity: { level: 'high' },
      webservice: { label: 'Transmettre au logiciel métier', url: 'https://', method: 'POST' },
    }
    setAjoutSur(null)
    setActionEditee({ ...base, ...(defauts[modele.type] || {}) })
  }

  return (
    <>
      <div className="ligne entete-parcours">
        <div className="champ compact">
          <label htmlFor="wf-initial">Point de départ</label>
          <select id="wf-initial" value={w.initial} disabled={lectureSeule}
                  onChange={(e) => onEnregistrer({ initial: e.target.value })}>
            {w.statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <p className="aide">Étape dans laquelle tombe un dossier au moment du dépôt.</p>
        </div>
      </div>

      {w.statuses.map((etape, index) => {
        const siennes = actions.filter((a) => a.from === etape.id)
        return (
          <section className="etape" key={etape.id}>
            <header>
              <span className="point" data-tone={etape.tone} />
              <h2>{etape.label}</h2>
              {etape.id === w.initial && <span className="tag">départ</span>}
              {etape.final && <span className="tag vert">fin du parcours</span>}
              {etape.visibility !== 'public' && <span className="tag gris">interne</span>}
              <div className="pousse" />
              {!lectureSeule && (
                <>
                  <span className="fleches">
                    <button aria-label="Monter l'étape" disabled={index === 0}
                            onClick={() => deplacerEtape(index, -1)}>▲</button>
                    <button aria-label="Descendre l'étape" disabled={index === w.statuses.length - 1}
                            onClick={() => deplacerEtape(index, 1)}>▼</button>
                  </span>
                  <button className="btn sm" onClick={() => setEtapeEditee(etape)}>Réglages</button>
                </>
              )}
            </header>
            {etape.help && <p className="aide">{etape.help}</p>}

            <ul className="actions-liste">
              {siennes.map((a) => (
                <li key={a.id}>
                  <span className={`kind ${a.kind}`}>
                    {a.kind === 'auto' ? 'tout seul' : 'bouton agent'}
                  </span>
                  <span className="texte">{phrase(a)}</span>
                  {a.when && <span className="condition">{a.when}</span>}
                  {!lectureSeule && (
                    <button className="btn sm" onClick={() => setActionEditee(a)}>Modifier</button>
                  )}
                </li>
              ))}
              {!siennes.length && (
                <li className="vide">Rien ne se passe ici. Le dossier resterait bloqué.</li>
              )}
            </ul>

            {!lectureSeule && (
              <button className="btn sm ajout" onClick={() => setAjoutSur(etape.id)}>
                Ajouter ce qui se passe ici
              </button>
            )}
          </section>
        )
      })}

      {!lectureSeule && (
        <button className="btn" onClick={() => setEtapeEditee({
          id: idNouveau('st'), label: 'Nouvelle étape', tone: 'n', visibility: 'public', help: '',
        })}>Ajouter une étape</button>
      )}

      {ajoutSur && (
        <div className="fenetre" role="dialog" aria-label="Ajouter une action">
          <div className="fenetre-corps large">
            <h2>Que se passe-t-il à l'étape « {etapeDe(ajoutSur)?.label} » ?</h2>
            {catalogue.families.map((f) => (
              <div key={f.famille} className="famille">
                <h3>{f.famille}</h3>
                <div className="grille">
                  {f.items.map((it) => (
                    <button className="tuile" key={it.type + it.kind}
                            onClick={() => nouvelleAction(it, ajoutSur)}>
                      <strong>{it.titre}</strong>
                      <span>{it.description}</span>
                      <span className={`kind ${it.kind}`}>
                        {it.kind === 'auto' ? 'tout seul' : 'bouton agent'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn" onClick={() => setAjoutSur(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {etapeEditee && (
        <EtapeForm etape={etapeEditee} catalogue={catalogue}
                   onEnregistrer={sauverEtape}
                   onSupprimer={w.statuses.some((s) => s.id === etapeEditee.id)
                     ? supprimerEtape : null}
                   onAnnuler={() => setEtapeEditee(null)} />
      )}

      {actionEditee && (
        <ActionForm action={actionEditee} etapes={w.statuses} catalogue={catalogue}
                    vocabulaire={vocabulaire} onEnregistrer={sauverAction}
                    onSupprimer={actions.some((a) => a.id === actionEditee.id)
                      ? supprimerAction : null}
                    onDupliquer={actions.some((a) => a.id === actionEditee.id)
                      ? dupliquerAction : null}
                    onAnnuler={() => setActionEditee(null)} />
      )}
    </>
  )
}

function Verifier({ problemes }) {
  if (!problemes.length) return (
    <div className="carte vide">
      <h2>Circuit cohérent</h2>
      <p>Chaque étape mène quelque part et l'habitant est tenu informé.</p>
    </div>
  )
  return (
    <div className="carte">
      <h2>{problemes.length} point{problemes.length > 1 ? 's' : ''} à regarder</h2>
      <ul className="controles">
        {problemes.map((p, i) => (
          <li key={i} className={p.level}>
            <span className="niveau">{p.level === 'err' ? 'à corriger' : 'à vérifier'}</span>
            <div><strong>{p.text}</strong><p className="aide">{p.detail}</p></div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Simulateur({ wfKey, vocabulaire }) {
  const [reponses, setReponses] = useState({})
  const [etapes, setEtapes] = useState([])
  const [resultat, setResultat] = useState(null)
  const [erreur, setErreur] = useState('')

  const jouer = async (steps = etapes) => {
    try {
      const r = await api.simulate(wfKey, { data: reponses, steps })
      setResultat(r); setEtapes(steps); setErreur('')
    } catch (e) { setErreur(e.message) }
  }

  useEffect(() => { jouer([]) }, [wfKey])   // eslint-disable-line

  return (
    <div className="deux">
      <section className="carte">
        <h2>Le dossier de test</h2>
        <p className="aide">Rien n'est enregistré, aucun courriel ne part réellement.</p>
        {erreur && <p className="erreur">{erreur}</p>}

        {resultat && (
          <>
            <p className="etat gros">{resultat.status?.label}</p>
            {resultat.criticity !== 'normal' && <p className="tag rouge">priorité {resultat.criticity}</p>}
            {resultat.alerts?.map((a, i) => <p className="alerte" key={i}>{a}</p>)}
            {resultat.awaiting && (
              <p className="attente-bloc">En attente de l'habitant : {resultat.awaiting.question}</p>
            )}

            <h3>Boutons disponibles pour l'agent</h3>
            <div className="ligne">
              {resultat.actions.length ? resultat.actions.map((a) => (
                <button key={a.id} className="btn"
                        onClick={() => jouer([...etapes, { action_id: a.id, comment: 'Motif de test' }])}>
                  {a.label}
                </button>
              )) : <p className="aide">Aucun bouton : le dossier est clos ou bloqué.</p>}
            </div>

            <h3>Ce qui s'est déclenché</h3>
            <ol className="trace">
              {resultat.trace.flat().map((e, i) => (
                <li key={i} className={e.public ? 'public' : 'interne'}>
                  <span className="kind">{e.kind}</span>
                  {e.label || e.title || e.mail_id || e.text || e.level || ''}
                  {!e.public && <span className="tag gris">interne</span>}
                </li>
              ))}
            </ol>
            <button className="btn" onClick={() => jouer([])}>Recommencer</button>
          </>
        )}
      </section>

      <aside className="carte">
        <h2>Réponses du formulaire</h2>
        {vocabulaire.length ? (
          <>
            <p className="aide">Ces réponses pilotent les aiguillages. Changez-les puis relancez
              pour vérifier les deux chemins.</p>
            {vocabulaire.slice(0, 8).map((v) => (
              <div className="champ" key={v.var}>
                <label>{v.label}</label>
                {v.options?.length ? (
                  <select value={reponses[v.var.replace('form_var_', '')] || ''}
                          onChange={(e) => setReponses({ ...reponses,
                            [v.var.replace('form_var_', '')]: e.target.value })}>
                    <option value="">Choisir…</option>
                    {v.options.map((o) => <option key={o}>{o}</option>)}
                  </select>
                ) : v.type === 'checkbox' ? (
                  <select value={reponses[v.var.replace('form_var_', '')] || 'Non'}
                          onChange={(e) => setReponses({ ...reponses,
                            [v.var.replace('form_var_', '')]: e.target.value })}>
                    <option>Oui</option><option>Non</option>
                  </select>
                ) : (
                  <input value={reponses[v.var.replace('form_var_', '')] || ''}
                         onChange={(e) => setReponses({ ...reponses,
                           [v.var.replace('form_var_', '')]: e.target.value })} />
                )}
              </div>
            ))}
            <button className="btn pri" onClick={() => jouer([])}>Relancer avec ces réponses</button>
          </>
        ) : <p className="aide">Aucune règle dans ce circuit : le parcours est le même pour tout le monde.</p>}
      </aside>
    </div>
  )
}
